import { useEffect, useState } from 'react';
import Script from 'next/script';

interface BookSchemaProps {
    publicacao: {
        id: number;
        titulo: string;
        descricao: string;
        capa: string;
        autores: string[];
        editora: string;
        isbn: string;
        edicao: string;
        anoPublicacao: string;
        idioma: string;
        paginas: number;
        categorias: string;
        possuiEpub?: boolean;
        possuiPdf?: boolean;
    };
}

const BookSchema = ({ publicacao }: BookSchemaProps) => {
    const [origin, setOrigin] = useState('');

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setOrigin(window.location.origin);
        }
    }, []);

    // Remove tags HTML da descrição para o schema
    const cleanDescription = publicacao.descricao
        ? publicacao.descricao.replace(/<[^>]*>/g, '').trim()
        : '';

    // Define o formato do livro
    const getBookFormat = () => {
        if (publicacao?.possuiEpub || publicacao?.possuiPdf) {
            return 'EBook';
        }
        return 'EBook';
    };

    // Define o encoding format (MIME type)
    const getEncodingFormat = () => {
        const formats = [];
        if (publicacao?.possuiPdf) formats.push('application/pdf');
        if (publicacao?.possuiEpub) formats.push('application/epub+zip');
        
        return formats.length > 1 ? formats : formats[0];
    };

    // Monta o schema JSON-LD
    const schemaData = {
        "@context": "https://schema.org",
        "@type": "Book",
        "@id": origin ? `${origin}/NossoAcervo/Publicacao/${publicacao.id}` : undefined,
        "name": publicacao.titulo,
        "author": publicacao.autores.map((autor) => ({
            "@type": "Person",
            "name": autor
        })),
        "publisher": {
            "@type": "Organization",
            "name": publicacao.editora
        },
        "isbn": publicacao.isbn,
        "bookEdition": `${publicacao.edicao}ª edição`,
        "bookFormat": getBookFormat(),
        "encodingFormat": getEncodingFormat(),
        "inLanguage": publicacao.idioma,
        "numberOfPages": publicacao.paginas,
        "datePublished": publicacao.anoPublicacao,
        "image": publicacao.capa,
        "description": cleanDescription,
        "genre": publicacao.categorias || undefined,
        "url": origin ? `${origin}/NossoAcervo/Publicacao/${publicacao.id}` : undefined
    };

    // Remove propriedades undefined
    const cleanedSchema = JSON.parse(
        JSON.stringify(schemaData, (key, value) => 
            value === undefined ? null : value
        )
    );

    // Usando Next.js Script component que é compatível com CSP
    return (
        <Script
            id={`book-schema-${publicacao.id}`}
            type="application/ld+json"
            strategy="beforeInteractive"
        >
            {JSON.stringify(cleanedSchema)}
        </Script>
    );
};

export default BookSchema;